// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Card, Typography } from '@mui/material';
import { NumericFormat } from 'react-number-format';
import { IconButton, Input } from '@shared/ui-kit';
import { DECIMAL_SEPARATORS } from '@shared/ui-kit/utils/constants';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { useDestinationFundItem } from './use-destination-fund-item';

export const DestinationFundItem = defineComponent({
  displayName: 'DestinationFundItem',
  useSetup: useDestinationFundItem,
  View: ({
    fund,
    onPercentageChange,
    onAmountChange,
    onPercentageBlur,
    onAmountBlur,
    onRemove,
    currencyPrefix,
  }) => (
    <Card variant="outlined" className="!rounded-lg !bg-white !p-3">
      <div className="flex items-center">
        <div className="flex min-w-0 max-w-[290px] flex-1 flex-col">
          <Typography variant="body2" className="truncate font-medium">
            {fund.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {fund.ticker ?? fund.isin}
          </Typography>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <NumericFormat
            customInput={Input}
            value={fund.percentage}
            onValueChange={({ floatValue }) => onPercentageChange(floatValue ?? 0)}
            onBlur={onPercentageBlur}
            className="w-[84px] text-center"
            data-testid="percentage-input"
            allowNegative={false}
            decimalScale={2}
            suffix=" %"
            allowedDecimalSeparators={DECIMAL_SEPARATORS}
          />

          <NumericFormat
            customInput={Input}
            value={fund.amount}
            onValueChange={({ floatValue }) => onAmountChange(floatValue ?? 0)}
            onBlur={onAmountBlur}
            className="w-[98px] text-center"
            data-testid="amount-input"
            prefix={currencyPrefix}
            allowNegative={false}
            decimalScale={0}
            fixedDecimalScale={false}
            thousandSeparator
            allowedDecimalSeparators={DECIMAL_SEPARATORS}
          />
        </div>

        <div className="ml-6">
          <IconButton
            icon="Trashcan"
            variant="icon"
            onClick={onRemove}
            data-testid="remove-button"
            color="danger"
          />
        </div>
      </div>
    </Card>
  ),
});
